import React, { useState, useEffect } from 'react';
import { getCompanyPayments } from '../services/paymentService';
import { formatAmountForDisplay } from '../services/stripeService';
import { Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Spinner, Flex } from '@chakra-ui/react';

const PaymentHistory = ({ payments, isLoading, onInitiatePayment }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        colorScheme: "yellow",
        label: "Pending"
      },
      succeeded: {
        colorScheme: "green",
        label: "Succeeded"
      },
      failed: {
        colorScheme: "red",
        label: "Failed"
      },
      refunded: {
        colorScheme: "purple",
        label: "Refunded"
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge 
        colorScheme={config.colorScheme} 
        borderRadius="full" 
        px={3} 
        py={1}
        textTransform="capitalize"
        fontWeight="medium"
      >
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" direction="column" py={10}>
        <Spinner 
          thickness="4px"
          speed="0.65s"
          color="#6c63ff"
          size="xl"
          mb={4}
        />
        <Text color="#6c63ff">Loading payment history...</Text>
      </Flex>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <Box 
        p={8} 
        borderRadius="12px" 
        bg="#f5f7ff" 
        textAlign="center"
      >
        <Text fontSize="xl" mb={3} fontWeight="medium">No payment history yet</Text>
        <Text color="#666" mb={6}>When you make payments to students, they will appear here.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md" color="#6c63ff">Payment History</Heading>
      </Flex>
      
      <Box 
        borderRadius="12px" 
        overflow="hidden" 
        boxShadow="0 4px 12px rgba(0,0,0,0.05)"
        bg="white"
      >
        <Table variant="simple">
          <Thead bg="#f5f7ff">
            <Tr>
              <Th>Date</Th>
              <Th>Student</Th>
              <Th>Project</Th>
              <Th isNumeric>Amount</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {payments.map(payment => (
              <Tr 
                key={payment.id}
                _hover={{ bg: "#f8fafc" }}
                transition="background-color 0.2s"
              >
                <Td>{formatDate(payment.createdAt)}</Td>
                <Td>
                  {payment.studentName || 
                    <Text as="span" color="gray.500">
                      {payment.studentId?.substring(0, 8) || 'Unknown'}
                    </Text>
                  }
                </Td>
                <Td>
                  {payment.projectName || 
                    <Text as="span" color="gray.500">
                      {payment.projectId?.substring(0, 8) || 'Unknown'}
                    </Text>
                  }
                </Td>
                <Td isNumeric fontWeight="medium">
                  {formatAmountForDisplay(payment.amount)}
                </Td>
                <Td>{getStatusBadge(payment.status)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default PaymentHistory; 